/**
 * useInvoiceOrderData — Fetches and processes order data for the invoice confirmation page.
 * Extracted from the legacy shared/services/orderService.ts god file.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Order } from '../../../shared/types/models';
import { getCart } from '../../cart/utils/cartUtil';
import { MIN_LOAD_TIME, ORDER_TEXTS, ORDER_FIELD_KEYS, ORDER_STORAGE_KEYS, ORDER_PARAM_KEYS, ORDER_ATTRIBUTE_KEYS } from '../constants/order.constants';
import { getOrderById, ensureOrderExists } from '../services/order.service';
import type {
    DisplayProduct,
    NextStep,
    HotelMapInfo,
    UseInvoiceOrderDataReturn,
} from '../types/order.types';

import { processLineItems, fetchNextSteps } from './useOrderData';


/**
 * Hook that fetches order data for invoice/pay-later confirmation.
 * Fetches by orderId query param or sessionStorage, processes line items,
 * and retrieves invoice-specific next steps and payment timeline.
 */
export const useInvoiceOrderData = (): UseInvoiceOrderDataReturn => {
    const [searchParams] = useSearchParams();

    const [order, setOrder] = useState<Order | null>(null);
    const [products, setProducts] = useState<DisplayProduct[]>([]);
    const [customerEmail, setCustomerEmail] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nextSteps, setNextSteps] = useState<NextStep[]>([]);
    const [paymentDueDuration, setPaymentDueDuration] = useState<string>(ORDER_TEXTS.DEFAULT_DUE_DURATION);
    const [hotelMapInfo, setHotelMapInfo] = useState<HotelMapInfo | null>(null);

    useEffect(() => {
        const fetchOrder = async () => {
            const startTime = Date.now();

            try {
                let orderId = searchParams.get(ORDER_PARAM_KEYS.ORDER_ID) || sessionStorage.getItem(ORDER_STORAGE_KEYS.ORDER_ID);
                const cartId = sessionStorage.getItem(ORDER_STORAGE_KEYS.CART_ID);

                // If no orderId but we have a cartId, try to find or create the order
                if (!orderId && cartId) {
                    try {
                        const cart = await getCart();
                        if (cart) {
                            const orderResult = await ensureOrderExists(cartId, cart.version, [], true);
                            if (orderResult) {
                                orderId = orderResult.id;
                                setOrder(orderResult);
                            }
                        }
                    } catch (e) {
                        console.error('Failed to ensure order exists during initialization:', e);
                        // Order existence check failed — fall through to searchParams/sessionStorage fallback
                    }
                }

                if (!orderId) {
                    setError(ORDER_TEXTS.ERRORS.ID_NOT_FOUND);
                } else {
                    const orderData = await getOrderById(orderId);

                    if (!orderData) {
                        setError(ORDER_TEXTS.NOT_FOUND);
                    } else {
                        setOrder(orderData);
                        setCustomerEmail(orderData.customerEmail || '');

                        // Process line items using shared helper
                        const { processedProducts, configProductId, hotelMapInfo: hotelInfo } = await processLineItems(orderData);
                        setProducts(processedProducts);
                        if (hotelInfo) setHotelMapInfo(hotelInfo);

                        // Fetch invoice-specific next steps and payment timeline
                        if (configProductId) {
                            try {
                                const steps = await fetchNextSteps(configProductId, ORDER_ATTRIBUTE_KEYS.INVOICE_NEXT_STEPS);
                                if (steps.length > 0) setNextSteps(steps);

                                // Fetch payment timeline from same config product
                                const { getProductById } = await import('../../cart/utils/cartUtil');
                                const product = await getProductById(configProductId);
                                if (product) {
                                    const productRec = product as unknown as { masterData?: { current?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } }; staged?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } } } };
                                    const current = productRec.masterData?.current || productRec.masterData?.staged;
                                    const attributes = current?.masterVariant?.attributes || [];
                                    const timelineAttr = (attributes as Array<{ name: string; value: unknown }>).find((a) => a.name === ORDER_FIELD_KEYS.INVOICE_TIMELINE);
                                    if (timelineAttr?.value) {
                                        setPaymentDueDuration(`${timelineAttr.value} days`);
                                    }
                                }
                            } catch (e) {
                                console.error('Failed to fetch secondary invoice confirmation steps:', e);
                                // Failed to fetch secondary invoice confirmation steps — component handles empty steps gracefully
                            }
                        }

                        // Clear cart from session storage after successful order
                        if (cartId) {
                            sessionStorage.removeItem(ORDER_STORAGE_KEYS.CART_ID);
                            localStorage.removeItem(ORDER_STORAGE_KEYS.CART_ID);
                            sessionStorage.removeItem(ORDER_STORAGE_KEYS.PRODUCT_STORE_MAP);
                            // Notify MiniBasket
                            import('../../cart/utils/cartUtil').then(({ emitCartUpdate }) => {
                                emitCartUpdate(null);
                            });
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to load invoice order details:', err);
                setError(ORDER_TEXTS.ERRORS.INVOICE_FETCH_FAILED);
            } finally {
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(0, MIN_LOAD_TIME - elapsedTime);
                setTimeout(() => {
                    setLoading(false);
                }, remainingTime);
            }
        };

        fetchOrder();
    }, [searchParams]);

    return { order, products, customerEmail, nextSteps, paymentDueDuration, hotelMapInfo, loading, error };
};
